import {Component, OnInit} from '@angular/core';
import {AgendaEventService} from '../../../../domain/agenda/service/agenda-event.service';
import {SafeSubscriberDirective} from '../../../../application/app-context/safe-subscriber.directive';
import {AgendaEvent} from '../../../../domain/agenda/model/agenda-event';
import {UserContext} from '../../../../application/app-context/user-context/user-context';
import {MatDialog} from '@angular/material/dialog';
import {CreateEventDialogComponent} from './dialog-create/create-event-dialog.component';
import {ConfigService} from '../../../../application/config/config.service';
import {UserContextService} from '../../../../application/app-context/user-context/user-context.service';
import moment from 'moment';
import {StringTransformer} from '../../../utils/string.utils';
import {IteratorUtils} from '../../../utils/iterator.utils';

export class YearMonth {
  year: number;
  month: number;


  constructor(year: number, month: number) {
    this.year = year;
    this.month = month;
  }
}

@Component({
             selector: 'app-agenda',
             templateUrl: './home-news-agenda.component.html',
             styleUrls: ['./home-news-agenda.component.scss']
           })
export class HomeNewsAgendaComponent extends SafeSubscriberDirective implements OnInit {

  readonly numberOfEventsDisplayed = 10;

  agendaEventsByYearMonth: Map<YearMonth, Map<Date, Array<AgendaEvent>>>;
  agendaEventsByDate: Map<Date, Array<AgendaEvent>>;
  isBackOffice: boolean;
  locale: string;
  eventAvailable = false;

  eventFailOver = false;

  compareAgendaEventKeyDate = (a: any, b: any) => {
    const aDate = a.key;
    const bDate = b.key;
    if (aDate.getTime() > bDate.getTime()) {
      return 1;
    } else if (aDate.getTime() < bDate.getTime()) {
      return -1;
    }
    return 0;
  };

  constructor(private agendaEventService: AgendaEventService,
              private configService: ConfigService,
              private userContextService: UserContextService,
              public dialog: MatDialog) {
    super();
  }

  ngOnInit(): void {
    const userContext: UserContext = this.userContextService.getUserContext();
    this.isBackOffice = this.initIsBackOffice(userContext);
    this.locale = userContext.locale;

    this.subs.push(
      this.agendaEventService.getAgendaEvents().subscribe((result: Array<AgendaEvent>) => {
        this.loadEvents(result);
        this.eventAvailable = !this.eventAvailable;
      }, error => {
        console.error(error);
        this.eventAvailable = !this.eventAvailable;
        this.eventFailOver = !this.eventFailOver;
      })
    );
  }

  quantityOfEventForYearAndMonth(dates: IterableIterator<Date>): number {
    return IteratorUtils.iterableSize(dates);
  }

  isAnyNextEvent(): boolean {
    return IteratorUtils.iterableSize(this.agendaEventsByYearMonth.entries()) > 0;
  }

  private initIsBackOffice(userContext: UserContext): boolean {
    if (this.configService.config.isAgendaEventAdministrationEnabledForAll) {
      return true;
    }

    return this.configService.config.agendaEventAdministratorUserLoginList
               .split(',')
               .some(v => v === userContext?.user.login);
  }

  public openNewEvent(): void {
    const dialogRef = this.dialog.open(CreateEventDialogComponent, {
      width: '1000px',
      height: '434px'
    });

    dialogRef.afterClosed().subscribe(() => {
      this.updateEvents();
    });
  }

  public updateEvents(): void {
    this.agendaEventService.getAgendaEvents().subscribe((result: Array<AgendaEvent>) => this.loadEvents(result));
  }

  public accordionTitleMonthYear(event: YearMonth): string {
    const date = moment(event.year + '-' + event.month + '-' + '01', 'YYYYMM').locale(this.locale);
    const currentYear = (new Date()).getFullYear();
    const month = new StringTransformer(date.format('MMMM')).capitalizeFirstLetters().build();
    if (currentYear !== event.year) {
      return month + ' ' + event.year;
    } else {
      return month;
    }
  }

  public getComparatorThatKeepOriginalOrderForMap() {
    return 1;
  }

  private buildAgendaEventByDate(agendaEvents: Array<AgendaEvent>): Map<Date, Array<AgendaEvent>> {
    const agendaEventsByDate = new Map<Date, Array<AgendaEvent>>();
    const eventDate = new Set(agendaEvents.map((event: AgendaEvent) => event.eventPeriod.startDate));

    eventDate.forEach((dateEvent: Date) => {
      agendaEventsByDate.set(dateEvent, agendaEvents.filter((event: AgendaEvent) => event.eventPeriod.startDate === dateEvent));
    });
    return agendaEventsByDate;
  }

  private buildNextAgendaEventByMonth(agendaEvents: Array<AgendaEvent>): void {
    const eventYearMonth = this.extractDistinctYearMonthFromEvents(agendaEvents);
    this.initAgendaEventsByYearMonth(eventYearMonth, agendaEvents);
  }

  private initAgendaEventsByYearMonth(eventYearMonth: YearMonth[], agendaEvents: Array<AgendaEvent>): void {
    this.agendaEventsByYearMonth = new Map<YearMonth, Map<Date, Array<AgendaEvent>>>();
    eventYearMonth.forEach(yearMonth => {
      const agendaEventsForYearAndMonth = agendaEvents.filter((event: AgendaEvent) => {
        return event.eventPeriod.startDate.getFullYear() === yearMonth.year
               && (event.eventPeriod.startDate.getMonth() + 1) === yearMonth.month;
      });
      const agendaEventsByDate = this.buildAgendaEventByDate(agendaEventsForYearAndMonth);
      if (IteratorUtils.iterableSize(agendaEventsByDate.keys()) > 0) {
        this.agendaEventsByYearMonth.set(yearMonth, agendaEventsByDate);
      }
    });
  }

  private extractDistinctYearMonthFromEvents(agendaEvents: Array<AgendaEvent>): YearMonth[] {
    return agendaEvents.map((event: AgendaEvent) => {
      const startDate = event.eventPeriod.startDate;
      return new YearMonth(startDate.getFullYear(), startDate.getMonth() + 1);
    }).reduce((acc: YearMonth[], current: YearMonth) => {
      const x = acc.find(item => item.year === current.year && item.month === current.month);
      if (!x) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    }, []);
  }

  private loadEvents(result: Array<AgendaEvent>): void {
    const firstAgendaEvents = result.slice(0, this.numberOfEventsDisplayed);
    this.agendaEventsByDate = this.buildAgendaEventByDate(firstAgendaEvents);
    const otherAgendaEvents = result.slice(this.numberOfEventsDisplayed);
    this.buildNextAgendaEventByMonth(otherAgendaEvents);
  }
}
